import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useDispatch } from 'react-redux';

import CallexLoader from '../../../components/CallexLoader';
import { vbCreditReportGet } from '../../../store/slices/voiceBroadcastBillingSlice';
import { formatTableDateTime } from '../../../utils/common';

const fmt = (v) =>
  Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Credit ledger for one company, newest first.
//
// Every row carries the balance before and after, so a mid-cycle top-up is
// visible in sequence rather than being folded into a running total.
const CreditLedgerModal = ({ isOpen, company, onCloseClick }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);

  const companyId = company?.company_id;

  const fetchLedger = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        vbCreditReportGet({ company_id: companyId, limit: 200 })
      ).unwrap();
      setRows(response?.data || []);
      setSummary(response?.summary || null);
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && companyId) fetchLedger();
  }, [isOpen, companyId]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">
          Credit Report — {company?.company_name || `Company #${companyId}`}
        </div>
      </ModalHeader>
      <ModalBody className="px-4">
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: 260 }}>
            <CallexLoader />
          </div>
        ) : (
          <>
            {summary && (
              <div className="d-flex gap-4 mb-3" style={{ fontSize: '14px' }}>
                <div>
                  Credit added: <strong className="text-success">{fmt(summary.credit_added)}</strong>
                </div>
                <div>
                  Credit used: <strong className="text-danger">{fmt(summary.credit_used)}</strong>
                </div>
                <div>
                  Entries: <strong>{summary.total}</strong>
                </div>
              </div>
            )}
            <div className="daily-report-table" style={{ maxHeight: '55vh' }}>
              <table className="report-list-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th className="text-end">Amount</th>
                    <th className="text-end">Balance Before</th>
                    <th className="text-end">Balance After</th>
                    <th>Reference</th>
                    <th>Description</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center">
                        No credit activity yet
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, i) => {
                      const isAdd = Number(r?.amount) > 0;
                      return (
                        <tr key={i}>
                          <td>{formatTableDateTime(r?.created_at)}</td>
                          <td>{isAdd ? 'Credit added' : 'Call charged'}</td>
                          <td className={`text-end fw-bold ${isAdd ? 'text-success' : 'text-danger'}`}>
                            {isAdd ? '+' : ''}
                            {fmt(r?.amount)}
                          </td>
                          <td className="text-end">{fmt(r?.balance_before)}</td>
                          <td className="text-end">{fmt(r?.balance_after)}</td>
                          <td>{r?.reference_id || '-'}</td>
                          <td>{r?.description || '-'}</td>
                          <td>{r?.initiated_by || '-'}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button onClick={() => onCloseClick()} className="px-5 bg-primary border-primary">
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

CreditLedgerModal.propTypes = {
  isOpen: PropTypes.bool,
  company: PropTypes.any,
  onCloseClick: PropTypes.func
};

export default CreditLedgerModal;
